import Extension from "../base/extension";

/**
 * Cart drawer upsell reveal.
 *
 * The upsell panel is rendered by the cart drawer's `cart_upsell` block but
 * stays hidden until the drawer is opened BY AN ADD TO CART, matching the
 * behaviour ported from the Bath & More theme: opening the drawer from the
 * header cart icon shows the cart alone, at its normal width.
 *
 * This is an extension and not a custom element on purpose. Extensions are
 * registered once at boot and live outside the drawer, whereas everything
 * inside `#cart-offcanvas-content` is destroyed and re-created on every cart
 * mutation (cart.js `renderMinicart()` swaps its outerHTML). An element in
 * there would have to win a race between its own async upgrade and the
 * `minicart:rendered` dispatch that follows the swap.
 *
 * The class lands on the <dialog>, which is NOT part of the swapped subtree, so
 * the reveal survives re-renders — a quantity change keeps the panel open. The
 * width and the panel visibility hang off that one class together (core.scss):
 * the drawer has a fixed `--dialog-drawer-width`, so hiding the panel alone
 * would leave an empty column behind.
 */
class CartUpsell extends Extension {
  name = "CartUpsell";

  init() {
    // Both listeners are on document rather than on the dialog: `dialog:closed`
    // bubbles (dialogs.js), so nothing here depends on the drawer existing at
    // boot. It does today — theme.core.min.js is deferred and the overlay group
    // renders in the body — but a section group is merchant-editable, and an
    // extension that silently disables itself for the session is a bad way to
    // find that out.
    document.addEventListener("minicart:rendered", this.reveal.bind(this));
    document.addEventListener("dialog:closed", this.hide.bind(this));
  }

  get dialog() {
    return document.querySelector("#cart-offcanvas");
  }

  reveal() {
    const dialog = this.dialog;
    // Queried per event, never cached: the panel is replaced on every render,
    // and it is absent entirely when the merchant has no cart_upsell block or
    // the source product resolves no complementary products.
    if (!dialog?.querySelector(".cart-upsell")) return;
    dialog.classList.add("upsell-visible");
  }

  hide(event) {
    // dialog:closed bubbles from every dialog in the theme; only ours matters.
    if (event?.detail?.dialog && event.detail.dialog !== this.dialog) return;
    this.dialog?.classList.remove("upsell-visible");
  }
}

export default CartUpsell;
